import React, { useEffect, useState } from "react";
import Select from "react-select";
import { axiosMaster } from "../api/axiosInstance";
import { useAuth } from "../auth/AuthContext";

const UserRole = () => {
  const { user } = useAuth();

  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleAccesses, setRoleAccesses] = useState([]);
  const [isMobileRole, setIsMobileRole] = useState(true);
  const [loading, setLoading] = useState(false);

  const [isFirstTime, setIsFirstTime] = useState(false);
  const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);

  /* ================= BUILD PAYLOAD ================= */
  const buildPayloadAccesses = (modules) => {
    return modules.map((m) => {
      let accessId = "";

      if (m.delete) accessId = `${m.moduleId}D`;
      else if (m.write) accessId = `${m.moduleId}W`;
      else if (m.read) accessId = `${m.moduleId}R`;

      return {
        moduleId: m.moduleId,
        moduleName: m.moduleName,
        category: m.category,
        accessId,
        read: m.read,
        write: m.write,
        delete: m.delete
      };
    });
  };




  /* ================= AUTO SAVE ================= */
  const autoSave = async (modules, roleId, mobileFlag = isMobileRole) => {
    try {
      if (!isInitialLoadDone) return;
      if (!roleId) return;

      const payload = {
        groupId: user.groupId,
        restId: user.restaurantId,
        userId: user.userId,
        roleId,
        userTypeId: user.userTypeId,
        isMobileRole: mobileFlag,
        loginUserId: user.userId,
        roleAccesses: buildPayloadAccesses(modules) // ✅ FULL LIST
      };

      console.log("FINAL PAYLOAD:", payload);

      await axiosMaster.post(`/UserRole/UpsertUserRole`, payload);
    } catch (err) {
      console.error("Auto save error:", err);
    }
  };

  /* ================= LOAD ================= */
  useEffect(() => {
    if (user?.userTypeId) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);

      /* 1️⃣ GET ROLES */
      const roleRes = await axiosMaster.get(
        `/RoleMaster/GetByUserType?userTypeId=${user.userTypeId}`
      );

      const roleOptions = roleRes.data.map((r) => ({
        value: r.roleId,
        label: r.roleName,
        data: r
      }));

      setRoles(roleOptions);

      /* 2️⃣ GET USER ROLE */
      const userRoleRes = await axiosMaster.get(
        `/UserRole/GetUserRole?groupId=${user.groupId}&restId=${user.restaurantId}&userId=${user.userId}`
      );

      if (userRoleRes?.data?.roleAccesses?.length > 0) {
        // ✅ EXISTING DATA → DO NOT UPSERT
        setIsFirstTime(false);

        const selected =
          roleOptions.find((r) => r.value === userRoleRes.data.roleId) ||
          roleOptions[0];

        setSelectedRole(selected);
        const roleModules = selected?.data?.roleAccesses || [];
        const userModules = userRoleRes.data.roleAccesses || [];

        // ✅ Merge both
        const mergedModules = roleModules.map((roleMod) => {
          const userMod = userModules.find(
            (u) => u.moduleId === roleMod.moduleId
          );

          return userMod
            ? userMod // already saved
            : {
              ...roleMod,
              read: false,
              write: false,
              delete: false,
              accessId: ""
            };
        });

        setRoleAccesses(mergedModules);
        setIsMobileRole(userRoleRes.data.isMobileRole ?? true);

        setIsInitialLoadDone(true);
      } else {
        // ❌ FIRST TIME ONLY
        setIsFirstTime(true);

        const defaultRole = roleOptions[0];
        setSelectedRole(defaultRole);

        const modules = defaultRole?.data?.roleAccesses || [];
        setRoleAccesses(modules);

        setIsInitialLoadDone(true);

        // ✅ FULL INSERT ONLY ONCE
        await axiosMaster.post(`/UserRole/UpsertUserRole`, {
          groupId: user.groupId,
          restId: user.restaurantId,
          userId: user.userId,
          roleId: defaultRole.value,
          userTypeId: user.userTypeId,
          isMobileRole,
          loginUserId: user.userId,
          roleAccesses: modules
        });
      }
    } catch (err) {
      console.error("Load error:", err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= ROLE CHANGE ================= */
  const handleRoleSelect = (role) => {
    setSelectedRole(role);

    const modules = role?.data?.roleAccesses || [];
    setRoleAccesses(modules);

    // ❌ NO API CALL HERE
  };

  /* ================= MOBILE TOGGLE ================= */
  const handleMobileToggle = async () => {
    const newValue = !isMobileRole;
    setIsMobileRole(newValue);

    await autoSave(roleAccesses, selectedRole?.value, newValue);
  };

  /* ================= TOGGLE ================= */
  const togglePermission = async (index, field) => {
    const updated = [...roleAccesses];
    const m = updated[index];

    if (field === "read") {
      m.read = !m.read;
      if (!m.read) {
        m.write = false;
        m.delete = false;
      }
    }

    if (field === "write") {
      m.write = !m.write;
      if (m.write) m.read = true;
      else m.delete = false;
    }

    if (field === "delete") {
      m.delete = !m.delete;
      if (m.delete) {
        m.read = true;
        m.write = true;
      }
    }

    setRoleAccesses(updated);

    // ✅ ALWAYS SEND FULL DATA
    await autoSave(updated, selectedRole?.value);
  };

  /* ================= UI ================= */
  return (
    <div className="max-w-5xl mx-auto bg-white shadow-lg rounded-lg p-6">
      <h2 className="text-xl font-bold mb-4">
        User Role (Auto Save System)
      </h2>

      {loading && <p>Loading...</p>}

      <div className="mb-4">
        <label className="font-medium mb-1 block">Select Role</label>
        <Select
          options={roles}
          value={selectedRole}
          onChange={handleRoleSelect}
        />
      </div>

      <div className="mb-4 flex items-center">
        <input
          type="checkbox"
          checked={isMobileRole}
          onChange={handleMobileToggle}
        />
        <label className="ml-2">Is Mobile Role</label>
      </div>

      {roleAccesses.length > 0 && (
        <table className="w-full border text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 text-left">Module</th>
              <th>Read</th>
              <th>Write</th>
              <th>Delete</th>
            </tr>
          </thead>

          <tbody>
            {roleAccesses.map((mod, i) => (
              <tr key={mod.moduleId}>
                <td className="p-2">{mod.moduleName}</td>

                <td className="text-center">
                  <input
                    type="checkbox"
                    checked={mod.read}
                    onChange={() => togglePermission(i, "read")}
                  />
                </td>

                <td className="text-center">
                  <input
                    type="checkbox"
                    checked={mod.write}
                    onChange={() => togglePermission(i, "write")}
                  />
                </td>

                <td className="text-center">
                  <input
                    type="checkbox"
                    checked={mod.delete}
                    onChange={() => togglePermission(i, "delete")}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default UserRole;